import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck, Database, HardDriveDownload, RotateCcw, AlertTriangle,
  Cloud, Lock, RefreshCw,
} from "lucide-react";

const STATE_STYLE = {
  HEALTHY: "bg-emerald-100 text-emerald-700",
  WARNING: "bg-amber-100 text-amber-700",
  FAILED: "bg-rose-100 text-rose-700",
  RESTORE_TEST_FAILED: "bg-rose-100 text-rose-700",
  "RESTORE TEST REQUIRED": "bg-amber-100 text-amber-700",
  CONFIGURATION_REQUIRED: "bg-slate-200 text-slate-600",
};

const fmtBytes = (n) => (n ? `${(n / 1048576).toFixed(1)} MB` : "—");
const fmtDate = (s) => (s ? s.slice(0, 16).replace("T", " ") : "—");

export default function BackupRecovery() {
  const [ov, setOv] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [o, l] = await Promise.all([
        api.get("/backup/overview"), api.get("/backup/list?limit=25"),
      ]);
      setOv(o.data); setRows(l.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reach the server — showing the last loaded status");
      setOv((prev) => prev || { health: "CONFIGURATION_REQUIRED", infrastructure: {} });
    }
  };
  useEffect(() => { load(); }, []);

  const waitFor = async (id) => {
    for (let i = 0; i < 240; i += 1) {
      await new Promise((r) => setTimeout(r, 5000));
      try {
        const { data } = await api.get("/backup/list?limit=25");
        setRows(data.items || []);
        const row = (data.items || []).find((b) => b.id === id);
        if (row && row.status !== "RUNNING") return row;
      } catch { /* transient: keep polling */ }
    }
    return null;
  };
  const backupNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/backup/run?kind=MANUAL");
      toast.info("Backup started — this page updates when it finishes");
      load();
      const row = await waitFor(data.id);
      if (!row) toast.warning("Backup still running — check the history row later");
      else if (row.status === "HEALTHY") toast.success(`Backup complete · ${row.total_docs} records`);
      else toast.error(`Backup failed: ${row.error || "see history"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Backup could not be started"); }
    finally { setBusy(false); }
  };
  const restoreTest = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/backup/${id}/restore-test`);
      toast.info("Restore test started in an isolated sandbox — this page updates when it finishes");
      let job = null;
      for (let i = 0; i < 360 && !job; i += 1) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const { data: j } = await api.get(`/backup/restore-test/${data.job_id}`);
          if (j.status !== "RUNNING") job = j;
        } catch { /* transient: keep polling */ }
      }
      if (!job) toast.warning("Restore test still running — check again later");
      else if (job.passed) toast.success(`Restore test PASSED · ${job.restored_total} rows`);
      else toast.error(`Restore test FAILED: ${job.error || "counts or checksum did not match"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Restore test could not be started"); }
    finally { setBusy(false); }
  };

  if (!ov) return <div className="p-8 text-slate-500">Loading Backup & Recovery…</div>;
  const infra = ov.infrastructure || {};

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="backup-recovery-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400">Security &amp; Continuity</div>
          <h1 className="text-2xl font-heading font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="h-6 w-6" /> Backup &amp; Recovery
          </h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="backup-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
          <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={backupNow} disabled={busy} data-testid="backup-now-btn">
            <HardDriveDownload className="h-4 w-4 mr-1" /> {busy ? "Working…" : "Backup now"}
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <span className="text-[13px] text-slate-600">Overall readiness</span>
        <span className={`px-3 py-1 rounded-full text-[12px] font-semibold ${STATE_STYLE[ov.health] || "bg-slate-100 text-slate-600"}`} data-testid="backup-health">
          {(ov.health || "").replace(/_/g, " ")}
        </span>
        {ov.backup_failures > 0 && <span className="text-[12px] text-rose-600 flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> {ov.backup_failures} failure(s)</span>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card icon={Database} title="Last database backup"
          value={fmtDate(ov.last_healthy_backup?.completed_at || ov.last_backup?.created_at)}
          sub={ov.last_backup ? `${ov.last_backup.total_docs || 0} records · ${fmtBytes(ov.last_backup.size_bytes)}` : "No backup yet"} />
        <Card icon={RotateCcw} title="Last restore test"
          value={ov.last_restore_test ? (ov.last_restore_test.passed ? "PASSED" : "FAILED") : "Not tested"}
          sub={ov.last_restore_test ? `${fmtDate(ov.last_restore_test.at)} · ${ov.last_restore_test.restored_total} rows` : "Run a restore test to validate"} />
        <Card icon={ShieldCheck} title="Objectives"
          value={`RPO ≤ ${ov.rpo_target_minutes}m · RTO ${ov.rto_target_hours}h`}
          sub={ov.tested_rto_minutes ? `Tested restore: ${ov.tested_rto_minutes.toFixed(1)} min` : "RTO not yet tested"} />
      </div>

      <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
        <div className="flex items-center gap-2 text-amber-800 font-semibold text-[13px]"><Cloud className="h-4 w-4" /> Infrastructure layer (platform-managed)</div>
        <p className="text-[12px] text-amber-700 mt-1">{infra.note}</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
          <InfraItem icon={Database} label="Point-in-time recovery" state={infra.point_in_time_recovery} />
          <InfraItem icon={Cloud} label="Offsite independent copy" state={infra.offsite_independent_copy} />
          <InfraItem icon={Lock} label="Immutable vault" state={infra.immutable_vault} />
        </div>
      </div>

      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-50 border-b text-[12px] font-semibold text-slate-600">Backup history</div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="text-slate-400 uppercase text-[10px] tracking-wider">
              <tr className="border-b border-slate-100">
                {["Date", "Type", "Status", "Records", "Size", "Encryption", "Integrity", "Expires", ""].map((h) => (
                  <th key={h} className="text-left px-4 py-2 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">No backups yet — click “Backup now”.</td></tr>
              ) : rows.map((b) => (
                <tr key={b.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`backup-row-${b.id}`}>
                  <td className="px-4 py-2">{fmtDate(b.completed_at || b.created_at)}</td>
                  <td className="px-4 py-2">{b.type}</td>
                  <td className="px-4 py-2" data-testid={`backup-status-${b.id}`} title={b.error || ""}><span className={`px-2 py-0.5 rounded-full text-[10px] ${STATE_STYLE[b.status] || "bg-slate-100 text-slate-600"}`}>{(b.status || "").replace(/_/g, " ")}</span></td>
                  <td className="px-4 py-2">{b.total_docs ?? "—"}</td>
                  <td className="px-4 py-2">{fmtBytes(b.size_bytes)}</td>
                  <td className="px-4 py-2 text-slate-500">{b.encryption ? "AES-256" : "—"}</td>
                  <td className="px-4 py-2">{b.integrity_verified ? "✓" : "—"}</td>
                  <td className="px-4 py-2 text-slate-500">{fmtDate(b.expires_at)}</td>
                  <td className="px-4 py-2 text-right">
                    {!["FAILED", "RUNNING"].includes(b.status) && (
                      <Button size="sm" variant="outline" className="h-7" disabled={busy} onClick={() => restoreTest(b.id)} data-testid={`restore-test-${b.id}`}>Restore test</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-[11px] text-slate-400">Logical backups are gzipped, checksummed JSON snapshots stored in object storage. Restore tests run in an isolated sandbox namespace and never touch production data. Certification also requires the platform-managed items above.</p>
    </div>
  );
}

function Card({ icon: Icon, title, value, sub }) {
  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-white">
      <div className="flex items-center gap-2 text-slate-500 text-[11px] uppercase tracking-wider"><Icon className="h-4 w-4" /> {title}</div>
      <div className="text-[18px] font-heading font-semibold text-slate-900 mt-1.5">{value}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}

function InfraItem({ icon: Icon, label, state }) {
  return (
    <div className="flex items-center justify-between bg-white/70 rounded px-2.5 py-1.5">
      <span className="flex items-center gap-1.5 text-[12px] text-slate-700"><Icon className="h-3.5 w-3.5" /> {label}</span>
      <span className={`px-2 py-0.5 rounded-full text-[9px] ${STATE_STYLE[state] || "bg-slate-200 text-slate-600"}`}>{(state || "—").replace(/_/g, " ")}</span>
    </div>
  );
}
